import { useEffect, useSyncExternalStore } from 'react';
import { locale, t, type MessageKey } from '../i18n/index.ts';
import { action } from './api.ts';
import { cityLine, type CountryRules } from './country.ts';
import { countryName } from './business.ts';

/**
 * The business's own tax rates (Settings → Tax rates), shared by every screen that asks for a
 * rate: the menu, invoices, quotes, rooms, repairs… Loaded once after sign-in and again after
 * a change. A record made with a rate that is now turned off still shows it, so it can be kept.
 */

export type TaxKind = 'standard' | 'reduced' | 'zero' | 'exempt';
export interface TaxRate {
  id: string;
  name: string;
  rate: number;
  kind: TaxKind;
  active: boolean;
  sequence: number;
}
export type { CountryRules } from './country.ts';
interface State {
  all: TaxRate[];
  rules: CountryRules | null;
  loaded: boolean;
}

let state: State = { all: [], rules: null, loaded: false };
let pending: Promise<void> | null = null;
const listeners = new Set<() => void>();
const set = (next: State) => {
  state = next;
  for (const l of listeners) l();
};

/** Asks the server for the rates; `force` after a change in Settings. */
export function loadTaxRates(force = false): Promise<void> {
  if (pending && !force) return pending;
  if (state.loaded && !force) return Promise.resolve();
  pending = action<{ rates: TaxRate[]; rules: CountryRules; paper: 'A4' | 'Letter' }>('company.tax_rates', {})
    .then((r) => {
      // Invoices and statements print on the business's paper (styles: [data-paper]).
      document.documentElement.dataset.paper = r.paper;
      set({ all: r.rates.map((x) => ({ ...x, rate: Number(x.rate) })), rules: r.rules, loaded: true });
    })
    .catch(() => undefined)
    .finally(() => {
      pending = null;
    });
  return pending;
}

/** Forgets the rates (signing out, another business). */
export function resetTaxRates() {
  set({ all: [], rules: null, loaded: false });
}

const active = (all: TaxRate[]) => all.filter((r) => r.active);

/** The usual rate now: the first standard one that is on, else the first on, else 0. */
export function standardRateNow(): number {
  const on = active(state.all);
  return (on.find((r) => r.kind === 'standard') ?? on[0])?.rate ?? 0;
}

/** "5.5 %" in the reader's number format. */
export const ratePct = (r: number) => `${new Intl.NumberFormat(locale(), { maximumFractionDigits: 2 }).format(r)} %`;

/** A rate the business has, or else its usual one (a product's rate when it is turned off, say). */
export function knownRateOr(r: number | null | undefined, fallback = standardRateNow()): number {
  return typeof r === 'number' && active(state.all).some((x) => Math.abs(x.rate - r) < 0.001) ? r : fallback;
}

export function useTaxRates() {
  const s = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
  );
  useEffect(() => {
    void loadTaxRates();
  }, []);
  const on = active(s.all);
  return {
    loaded: s.loaded,
    all: s.all,
    rules: s.rules,
    /** The rates to pick from: those that are on, plus `current` when a record still has an old one. */
    choices(current?: number | null): number[] {
      const list = [...new Set(on.map((r) => r.rate))];
      if (typeof current === 'number' && !list.some((r) => Math.abs(r - current) < 0.001)) list.push(current);
      return list;
    },
    standard: (on.find((r) => r.kind === 'standard') ?? on[0])?.rate ?? 0,
  };
}

/** What a line shows as its price: with VAT in it, or before sales tax where that is added. */
export const linePrice = (l: { total: number; tax?: number; tax_added?: boolean }) => (l.tax_added ? l.total - (l.tax ?? 0) : l.total);

/** What the business's own tax number is called (its country pack: NIPT, EIN, SIRET…), for printed documents. */
export const ownTaxLabel = (): string => state.rules?.taxId.label ?? t('country.tax_no');

const BUYER_ID_TYPES = ['NUIS', 'ID', 'PASS', 'VAT', 'TAX', 'SOC'];

/**
 * What a customer's number is called on a document: the type chosen on the customer, else the
 * business's own tax number name when the customer is in the same country, else "Tax number".
 */
export function buyerIdLabel(c: { id_type?: string | null; country?: string | null }): string {
  if (c.id_type && c.id_type !== 'TAX' && BUYER_ID_TYPES.includes(c.id_type)) return t(`inv.id_${c.id_type}` as MessageKey);
  if (c.id_type && c.id_type !== 'TAX') return c.id_type;
  return !c.country || c.country.toUpperCase() === state.rules?.code ? ownTaxLabel() : t('country.tax_no');
}

/** The business's own country pack, once loaded after sign-in. */
export const businessRules = () => state.rules;

/** "16192 Coastal Highway, Lewes, DE 19958": street, then city, postcode and state as the business's country writes them. */
export function addressLine(c: { address?: string | null; city?: string | null; postcode?: string | null; region?: string | null }): string {
  return [c.address, cityLine(state.rules, c)].filter(Boolean).join(', ');
}

/** A buyer's address on a document: the country is named only when it is not the business's own. */
export function buyerAddress(b: { address?: string | null; town?: string | null; country?: string | null }): string {
  const abroad = b.country && state.rules && b.country !== state.rules.code ? countryName(b.country) : null;
  return [b.address, b.town, abroad].filter(Boolean).join(', ');
}

/** The business's numbers as its country names them: tax number, VAT number, register number. */
export function ownIdLines(c: { tax_id?: string | null; vat_id?: string | null; reg_no?: string | null }): string[] {
  const r = state.rules;
  const out: string[] = [];
  if (c.tax_id) out.push(t('inv.p_tax', { label: ownTaxLabel(), id: c.tax_id }));
  if (c.vat_id) out.push(t('inv.p_tax', { label: r?.vatId?.label ?? t('business.vat_id'), id: c.vat_id }));
  if (c.reg_no) out.push(t('inv.p_tax', { label: r?.regNo?.label ?? t('business.reg_no'), id: c.reg_no }));
  return out;
}

/**
 * The notes the law of the business's country asks for on an invoice (its country pack): reverse
 * charge for a business customer elsewhere in the EU when no VAT is charged, and late payment
 * terms on business invoices where the country asks for them (France), and the small business
 * note when server administration put the business in that scheme.
 */
export function invoiceLegalNotes(
  buyer: { country?: string | null; tax_id?: string | null },
  inv: { tax: number; kind?: string },
  euCountries: readonly string[],
): string[] {
  const r = state.rules;
  if (!r) return [];
  const notes: string[] = [];
  const country = (buyer.country ?? r.code).toUpperCase();
  const business = !!buyer.tax_id;
  if (r.mentions.reverseCharge && business && inv.tax === 0 && country !== r.code && euCountries.includes(country) && euCountries.includes(r.code)) notes.push(r.mentions.reverseCharge);
  if (r.mentions.latePayment && business && inv.kind !== 'credit') notes.push(r.mentions.latePayment);
  if (r.smallBusinessScheme && r.mentions.smallBusiness) notes.push(r.mentions.smallBusiness);
  return notes;
}
